import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BookOpen, Lock, RotateCcw } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { GradebookFromQuery } from './classDetailTypes';
import {
  useFinalizeClassGradebooks,
  useFinalizeGradebook,
  useReopenGradebook,
} from './useGradebookManagement';

interface Props {
  classOfferingId: string;
  gradebooks: ReadonlyArray<GradebookFromQuery>;
  canManage: boolean;
  courseGradebooksPath?: string;
  onChanged: () => void | Promise<unknown>;
}

type FinalizationTarget =
  | { action: 'finalize' | 'reopen'; gradebook: GradebookFromQuery }
  | { action: 'finalizeAll' };

export default function GradebookManagementSection({
  classOfferingId,
  gradebooks,
  canManage,
  courseGradebooksPath,
  onChanged,
}: Props) {
  const navigate = useNavigate();
  const [finalizeClassGradebooks] = useFinalizeClassGradebooks();
  const [finalizeGradebook] = useFinalizeGradebook();
  const [reopenGradebook] = useReopenGradebook();
  const [finalizationTarget, setFinalizationTarget] = useState<FinalizationTarget | null>(null);
  const [finalizationSaving, setFinalizationSaving] = useState(false);
  const [finalizationError, setFinalizationError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [refreshStale, setRefreshStale] = useState(false);
  const actionSequence = useRef(0);
  const activeClassOfferingId = useRef(classOfferingId);
  activeClassOfferingId.current = classOfferingId;

  useEffect(() => {
    actionSequence.current += 1;
    setFinalizationTarget(null);
    setFinalizationSaving(false);
    setFinalizationError(null);
    setRefreshError(null);
    setSuccessNotice(null);
    setRefreshStale(false);

    return () => {
      actionSequence.current += 1;
    };
  }, [classOfferingId]);

  const orderedGradebooks = useMemo(
    () => [...gradebooks].sort((a, b) => a.name.localeCompare(b.name)),
    [gradebooks]
  );
  const pointsGradebooks = orderedGradebooks.filter((gradebook) => gradebook.gradingMode === 'POINTS');
  const openPointsGradebooks = pointsGradebooks.filter((gradebook) => !gradebook.finalized);
  const standardsGradebookCount = orderedGradebooks.length - pointsGradebooks.length;
  const batchPreviewsReady = openPointsGradebooks.every(
    (gradebook) => gradebook.finalizationPreview.ready
  );
  const userErrorMessage = (errors: ReadonlyArray<{ message: string }> | null | undefined) =>
    errors?.map((item) => item.message).join(', ') || null;

  const openFinalizationAction = (
    action: 'finalize' | 'reopen',
    gradebook: GradebookFromQuery,
  ) => {
    setFinalizationError(null);
    setFinalizationTarget({ action, gradebook });
  };

  const actionIsCurrent = (sequence: number, offeringId: string) =>
    actionSequence.current === sequence && activeClassOfferingId.current === offeringId;

  const refreshCommittedFinalization = async (
    savedMessage: string,
    sequence: number,
    offeringId: string,
  ) => {
    if (!actionIsCurrent(sequence, offeringId)) return;

    setFinalizationTarget(null);
    setSuccessNotice(savedMessage);
    try {
      await onChanged();
      if (!actionIsCurrent(sequence, offeringId)) return;
      setRefreshError(null);
      setRefreshStale(false);
    } catch {
      if (!actionIsCurrent(sequence, offeringId)) return;
      setRefreshStale(true);
      setRefreshError(
        `${savedMessage} Current data could not be refreshed; reload the class before making more grading changes.`
      );
    }
  };

  const handleFinalizationAction = async () => {
    if (!finalizationTarget) return;

    const target = finalizationTarget;
    const offeringId = classOfferingId;
    const sequence = ++actionSequence.current;
    setFinalizationSaving(true);
    setFinalizationError(null);
    try {
      if (target.action === 'finalizeAll') {
        const result = await finalizeClassGradebooks({
          variables: { input: { classOfferingId: offeringId } },
        });
        if (!actionIsCurrent(sequence, offeringId)) return;

        const payload = result.data?.finalizeClassGradebooks;
        const message = userErrorMessage(payload?.userErrors);
        if (message) {
          setFinalizationError(`${message} No gradebooks were finalized.`);
          return;
        }

        const finalizedCount = payload?.finalizedCount ?? openPointsGradebooks.length;
        const alreadyFinalizedCount = payload?.alreadyFinalizedCount ??
          pointsGradebooks.length - openPointsGradebooks.length;
        await refreshCommittedFinalization(
          `${finalizedCount} gradebook${finalizedCount === 1 ? '' : 's'} finalized. ` +
          `${alreadyFinalizedCount} already finalized and left unchanged.`,
          sequence,
          offeringId,
        );
      } else if (target.action === 'finalize') {
        const result = await finalizeGradebook({
          variables: { input: { id: target.gradebook.id } },
        });
        if (!actionIsCurrent(sequence, offeringId)) return;

        const message = userErrorMessage(result.data?.finalizeGradebook?.userErrors);
        if (message) {
          setFinalizationError(message);
          return;
        }
        await refreshCommittedFinalization(
          `${target.gradebook.name} was finalized.`,
          sequence,
          offeringId,
        );
      } else {
        const result = await reopenGradebook({
          variables: { input: { id: target.gradebook.id } },
        });
        if (!actionIsCurrent(sequence, offeringId)) return;

        const message = userErrorMessage(result.data?.reopenGradebook?.userErrors);
        if (message) {
          setFinalizationError(message);
          return;
        }
        await refreshCommittedFinalization(
          `${target.gradebook.name} was reopened.`,
          sequence,
          offeringId,
        );
      }
    } catch (caught) {
      if (!actionIsCurrent(sequence, offeringId)) return;
      setFinalizationError(
        caught instanceof Error ? caught.message : 'Unable to change finalization status.',
      );
    } finally {
      if (actionIsCurrent(sequence, offeringId)) setFinalizationSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Academic Gradebooks ({gradebooks.length})
            </CardTitle>
            <CardDescription>
              These are the gradebooks this class's course defines. Add, rename or
              reweight them on the course. Every active student on the class roster
              belongs to every gradebook; here you finalize the term.
            </CardDescription>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={
                  finalizationSaving ||
                  refreshStale ||
                  openPointsGradebooks.length === 0 ||
                  !batchPreviewsReady
                }
                title={
                  pointsGradebooks.length === 0
                    ? 'This class has no points gradebooks to finalize'
                    : openPointsGradebooks.length === 0
                      ? 'Every points gradebook is already finalized'
                      : !batchPreviewsReady
                        ? 'Resolve every gradebook preview error before finalizing all'
                        : undefined
                }
                onClick={() => {
                  setFinalizationError(null);
                  setFinalizationTarget({ action: 'finalizeAll' });
                }}
              >
                <Lock className="mr-1 h-4 w-4" />
                Finalize All
              </Button>
              {courseGradebooksPath && (
                <Button variant="outline" size="sm" onClick={() => navigate(courseGradebooksPath)}>
                  <BookOpen className="mr-1 h-4 w-4" />
                  Manage on course
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {successNotice && <p role="status" className="text-sm text-emerald-700">{successNotice}</p>}
        {refreshError && (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {refreshError}
          </p>
        )}
        {orderedGradebooks.map((gradebook) => {
          const memberNames = gradebook.memberships
            .filter(({ rosterEntry }) => rosterEntry.state === 'ACTIVE')
            .map(({ rosterEntry }) =>
              `${rosterEntry.person.firstName} ${rosterEntry.person.lastName}`
            );
          const preview = gradebook.finalizationPreview;
          const currentFinalization = gradebook.currentFinalization;

          return (
            <div key={gradebook.id} className="rounded-lg border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{gradebook.name}</span>
                    {gradebook.finalized && (
                      <Badge className="gap-1">
                        <Lock className="h-3 w-3" />
                        Finalized r{currentFinalization?.revision}
                      </Badge>
                    )}
                    <Badge variant="outline">
                      {gradebook.categories.length === 0
                        ? 'Total points'
                        : `${gradebook.categories.length} categories`}
                    </Badge>
                  </div>
                  {gradebook.gradingMode === 'POINTS' ? (
                    <p className="text-sm text-muted-foreground">
                      Transcript course: {gradebook.transcriptCourse?.name ?? gradebook.transcriptName}
                      {gradebook.creditValue == null ? '' : ` · ${gradebook.creditValue} credits`}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Standards assessment
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {memberNames.length === 0
                      ? 'No active students on the class roster'
                      : `${memberNames.length} roster members: ${memberNames.join(', ')}`}
                  </p>
                  {gradebook.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1" aria-label={`${gradebook.name} categories`}>
                      {gradebook.categories.map((category) => (
                        <Badge key={category.id} variant="secondary" className="gap-1">
                          {category.locked && <Lock className="h-3 w-3" />}
                          {category.name}
                          {category.locked && ' · Locked'}
                          {category.locked && category.lockedByGradebookNames.length > 0 && (
                            <span className="sr-only">
                              {' '}by {category.lockedByGradebookNames.join(', ')}
                            </span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {gradebook.gradingMode === 'POINTS' ? (
                <div className="mt-4 space-y-3 border-t pt-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Term finalization preview</p>
                      <p className="text-xs text-muted-foreground">
                        {gradebook.finalized && currentFinalization
                          ? `Revision ${currentFinalization.revision} locked ${currentFinalization.resultCount} student results on ${new Date(String(currentFinalization.finalizedAt)).toLocaleDateString()}.`
                          : `Revision ${preview.nextRevision} will snapshot every historically eligible student.`}
                      </p>
                    </div>
                    {canManage && (
                      gradebook.finalized ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={refreshStale}
                          onClick={() => openFinalizationAction('reopen', gradebook)}
                        >
                          <RotateCcw className="mr-1 h-4 w-4" />
                          Reopen
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          disabled={!preview.ready || refreshStale}
                          onClick={() => openFinalizationAction('finalize', gradebook)}
                        >
                          <Lock className="mr-1 h-4 w-4" />
                          Finalize revision {preview.nextRevision}
                        </Button>
                      )
                    )}
                  </div>

                  {preview.errors.length > 0 ? (
                    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                      {preview.errors.map((message) => <p key={message}>{message}</p>)}
                    </div>
                  ) : preview.students.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No historically eligible students are in this gradebook.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-md border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="p-2 text-left font-medium">Student</th>
                            <th className="p-2 text-right font-medium">Percent</th>
                            <th className="p-2 text-right font-medium">Grade</th>
                            <th className="p-2 text-right font-medium">GPA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.students.map((student) => (
                            <tr key={student.person.id} className="border-b last:border-0">
                              <td className="p-2">
                                {student.person.lastName}, {student.person.firstName}
                              </td>
                              <td className="p-2 text-right">
                                {student.percentage == null ? '—' : `${student.percentage.toFixed(2)}%`}
                              </td>
                              <td className="p-2 text-right">{student.letterGrade ?? '—'}</td>
                              <td className="p-2 text-right">
                                {student.gpaPoints == null ? '—' : student.gpaPoints.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-4 border-t pt-4 text-sm text-muted-foreground">
                  Standards assessments are not finalized into letter-grade transcripts.
                </p>
              )}
            </div>
          );
        })}
        {!canManage && (
          <p className="text-xs text-muted-foreground">
            Gradebook setup is managed by school administrators on the course.
          </p>
        )}
      </CardContent>

      <AlertDialog
        open={!!finalizationTarget}
        onOpenChange={(open) => {
          if (!open && !finalizationSaving) {
            setFinalizationTarget(null);
            setFinalizationError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {finalizationTarget?.action === 'finalizeAll'
                ? 'Finalize all points gradebooks'
                : finalizationTarget?.action === 'finalize'
                  ? 'Finalize term grades'
                  : 'Reopen term grades'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {finalizationTarget?.action === 'finalizeAll'
                ? `Create immutable revisions for ${openPointsGradebooks.length} open points gradebook${openPointsGradebooks.length === 1 ? '' : 's'}? ` +
                  `${pointsGradebooks.length - openPointsGradebooks.length} already finalized gradebook${pointsGradebooks.length - openPointsGradebooks.length === 1 ? '' : 's'} will stay unchanged` +
                  `${standardsGradebookCount > 0 ? `, and ${standardsGradebookCount} standards gradebook${standardsGradebookCount === 1 ? '' : 's'} will be excluded` : ''}. ` +
                  'The batch is all-or-nothing.'
                : finalizationTarget?.action === 'finalize'
                ? `Create immutable revision ${finalizationTarget.gradebook.finalizationPreview.nextRevision} for ${finalizationTarget.gradebook.name}? Scores, assignments, categories, membership, and transcript metadata will be locked until an administrator reopens it.`
                : finalizationTarget?.action === 'reopen'
                  ? `Reopen ${finalizationTarget.gradebook.name}? Its prior revision remains immutable in history, but it will stop appearing on transcripts until a new revision is finalized.`
                  : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {finalizationError && <p role="alert" className="text-sm text-destructive">{finalizationError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={finalizationSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={finalizationSaving}
              onClick={(event) => {
                event.preventDefault();
                void handleFinalizationAction();
              }}
            >
              {finalizationSaving
                ? 'Saving…'
                : finalizationTarget?.action === 'finalizeAll'
                ? 'Finalize All'
                : finalizationTarget?.action === 'finalize'
                ? 'Finalize grades'
                : 'Reopen grades'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
